import { type TFunction } from "i18next";

/**
 * De foutcodes die het accepteren van een medewerkersuitnodiging kan opleveren, en waar ze vandaan
 * komen.
 *
 * WAAROM DEZE LIJST BESTAAT De app zocht de code in `error.message` en op `error.code`, en op
 * allebei die plekken staat hij niet: `message` draagt de leesbare zin ("Account exists password
 * required") en de code staat op `error.cause.code`, omdat `extractResponseData` het
 * antwoordlichaam als `cause` in een `FetchError` hangt. Elke `includes(CODE)`-vergelijking was
 * daardoor onwaar en iedereen kreeg dezelfde generieke toast (#221).
 *
 * HOE EEN CODE ONTSTAAT De api gooit `new APIError(status, { message })` en better-call leidt de
 * code van die zin af: `message.toUpperCase().replace(/ /g, "_").replace(/[^A-Z0-9_]/g, "")`. De
 * sleutel in `BASE_ERROR_CODES` is dus niet wat je krijgt; de waarde is dat. Dat verschil is hier
 * niet theoretisch: `accept-invite.employee.plugin.ts` gooit voor een verlopen uitnodiging de zin
 * "Invitation has expired" en niet `BASE_ERROR_CODES.INVITATION_EXPIRED` ("Invitation expired"), en
 * dat levert `INVITATION_HAS_EXPIRED` op.
 *
 * Elke code hier heeft een gelijknamige sleutel in `error-codes.json` (nl en en);
 * `accept-invite-error.test.ts` bewaakt dat in beide talen.
 */

/**
 * Wat `/accept-invite/employee` vandaag echt terugstuurt, afgeleid uit de zinnen in
 * `accept-invite.employee.plugin.ts` en `shared/employee-invitation-identity.ts`.
 */
const CODES_FROM_THE_EMPLOYEE_ENDPOINT = [
  /** `password.length < 8` in de plugin, vóór alles anders. */
  "PASSWORD_TOO_SHORT",
  /** Het token hoort bij geen enkele uitnodiging (meer). */
  "INVITATION_NOT_FOUND",
  /** "Invitation has expired", dus met `HAS` erin, zie hierboven. */
  "INVITATION_HAS_EXPIRED",
  /**
   * Fase 5 van "één mens, één login" (admin#684): dit adres heeft al een Boomer-account, dus de
   * uitnodiging voegt een lidmaatschap toe en vraagt om het wachtwoord van dat account. Precies de
   * melding waarvoor #221 is gemeld.
   */
  "ACCOUNT_EXISTS_PASSWORD_REQUIRED",
  /** De voorcontrole op het telefoonnummer bij het activeren van een bestaande identiteit (400). */
  "PHONE_NUMBER_IS_ALREADY_IN_USE_BY_ANOTHER_ACCOUNT",
  /** De unique-constraint op het telefoonnummer (409). */
  "PHONE_NUMBER_IS_ALREADY_IN_USE",
  /** De unique-constraint op het e-mailadres (409). */
  "EMAIL_IS_ALREADY_IN_USE",
  /** Een unique-constraint waarvan we het veld niet kennen (409). */
  "A_USER_WITH_THIS_INFORMATION_ALREADY_EXISTS",
  /** De uitnodiging is verwerkt, maar er kwam geen sessie uit. */
  "FAILED_TO_CREATE_SESSION",
] as const;

/**
 * Codes uit `BASE_ERROR_CODES` voor dezelfde gevallen. Het relatie-endpoint gooit ze al wél
 * (`accept-invite.relation.plugin.ts` gebruikt `BASE_ERROR_CODES.PHONE_ALREADY_EXISTS`), en de api
 * zet er in dat bestand zelf bij dat de losse zinnen de bedoeling niet zijn. Wordt het
 * medewerkers-endpoint een keer gelijkgetrokken, dan blijft de melding staan in plaats van stil
 * terug te vallen op de generieke toast.
 */
const CODES_THE_API_MAY_SWITCH_TO = [
  "INVITATION_EXPIRED",
  "EMAIL_ALREADY_EXISTS",
  "PHONE_ALREADY_EXISTS",
  "USER_ALREADY_EXISTS",
] as const;

export const ACCEPT_INVITE_ERROR_CODES = [
  ...CODES_FROM_THE_EMPLOYEE_ENDPOINT,
  ...CODES_THE_API_MAY_SWITCH_TO,
] as const;

export type AcceptInviteErrorCode = (typeof ACCEPT_INVITE_ERROR_CODES)[number];

/**
 * De codes die over hetzelfde gaan: dit telefoonnummer hangt al aan een ander account. Die krijgen
 * geen toast maar het venster op het scherm zelf, want de weg eruit loopt via iemand anders (je
 * manager) en dat is meer dan drie seconden lezen.
 */
export const PHONE_IN_USE_ERROR_CODES: readonly string[] = [
  "PHONE_NUMBER_IS_ALREADY_IN_USE",
  "PHONE_NUMBER_IS_ALREADY_IN_USE_BY_ANOTHER_ACCOUNT",
  "PHONE_ALREADY_EXISTS",
];

/**
 * De code uit een fout vissen, ongeacht in welke vorm hij binnenkomt.
 *
 * `FetchError` hangt het antwoordlichaam onder `cause`, better-auth-fouten dragen `code` direct, en
 * een enkel backend-pad noemt het veld `errorCode`. Alle vier de plekken worden nagelopen; de
 * eerste niet-lege string wint.
 */
export function extractErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== "object") {
    return undefined;
  }

  const candidate = error as {
    code?: unknown;
    errorCode?: unknown;
    cause?: { code?: unknown; errorCode?: unknown } | null;
  };

  const candidates = [
    candidate.code,
    candidate.errorCode,
    candidate.cause?.code,
    candidate.cause?.errorCode,
  ];

  for (const value of candidates) {
    if (typeof value === "string" && value.length > 0) {
      return value;
    }
  }

  return undefined;
}

/** Gaat deze fout over een telefoonnummer dat al aan een ander account hangt? */
export function isPhoneInUseError(error: unknown): boolean {
  const code = extractErrorCode(error);
  return code !== undefined && PHONE_IN_USE_ERROR_CODES.includes(code);
}

/**
 * De vertaalde melding bij een mislukte uitnodiging, of de generieke zin als de code onbekend is of
 * ontbreekt.
 *
 * Onbekend kán gewoon voorkomen: de plugin gooit ook zinnen met een veldnaam erin ("Required
 * registration field 'Bankrekening' is missing"), en daar valt geen vaste sleutel van te maken.
 */
export function resolveAcceptInviteErrorMessage(error: unknown, t: TFunction): string {
  const code = extractErrorCode(error);

  if (code) {
    const translated = t(`error-codes:${code}`, { defaultValue: "" });
    if (typeof translated === "string" && translated.length > 0) {
      return translated;
    }
  }

  return t("actions:accept-invite.error");
}
